import { faCalculator } from "@fortawesome/pro-duotone-svg-icons";
import { Button } from "@progress/kendo-react-buttons";
import { TreeView } from "@progress/kendo-react-treeview";
import { useEffect, useState } from "react";
import { CodeEditor } from "../../CodeEditor/CodeEditor.tsx";
import {
  columnsConstructorSessionData,
  editedPropertyValues as editedColumnPropertyValues,
  selectedColumnFieldPropertyValues,
  selectedColumnIndex,
} from "../../Fields/ColumnsConstructor/ColumnsConstructorSignals";
import {
  isGalleryEditor,
  selectedFieldPropertyValues,
  selectedFieldName as selectedTemplateFieldName,
  templateData,
} from "../../Template/NewTemplateSignals";
import { componentTree } from "../ExpressionTrees/ComponentTree";
import { definitionsTree } from "../ExpressionTrees/DefinitionTree";
import { masterTree } from "../ExpressionTrees/MasterTree";
import {
  editedPropertyValues,
  galSessionData,
  selectedFieldName,
  selectedGalleryFieldPropertyValues,
} from "../GalleryConstructor/GalleryConstructorSignals";
import { allDatasets, expressionBuilderVisibility } from "../Store/CommonSignals";
import { ColorChangingIcon } from "../UI/ColorChangingIcon";
import "./ExpressionBuilder.css";

const CustomItemRender = (props, setInternalExpression, editor) => {
  const onChildDoubleClick = (item, setInternalExpression) => {
    setInternalExpression((prevState) => {
      if (prevState === undefined) {
        return item.expressionText;
      }
      return prevState + " " + item.expressionText;
    });

    const insertTextAtCursor = (text) => {
      if (!editor) return;

      const selection = editor.getSelection();
      if (selection) {
        editor.executeEdits("external", [
          {
            range: selection,
            text: text,
            forceMoveMarkers: true,
          },
        ]);
      } else {
        const position = editor.getPosition() || { lineNumber: 1, column: 1 };
        editor.executeEdits("external", [
          {
            range: {
              startLineNumber: position.lineNumber,
              startColumn: position.column,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            },
            text: text,
            forceMoveMarkers: true,
          },
        ]);
      }
      editor.focus();
    };
    insertTextAtCursor(item.expressionText);
  };

  return (
    <div className="align-columns">
      {props.item.fontawesomeIcon && (
        <div style={{ width: "20px", justifyContent: "center", display: "flex" }}>
          <ColorChangingIcon
            icon={props.item.fontawesomeIcon}
            initialPrimaryColor="#605e5c"
            initialSecondaryColor="#0078D4"
            initialSize={16}
            hoverSize={16}
          />
        </div>
      )}
      <div onDoubleClick={() => onChildDoubleClick(props.item, setInternalExpression)}>
        {props.item.text}
      </div>
    </div>
  );
};

function ParameterSetsHelper() {
  const datasets = allDatasets.value;
  return datasets.filter((dataset) => dataset.datasetType === "Global Parameters");
}

function ExpressionFieldsHelper() {
  let fieldNamesTemp = [];
  templateData.value?.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field?.fieldName !== selectedTemplateFieldName.value) {
        fieldNamesTemp.push({
          text: field.fieldName,
          math: "Fields",
          expressionText: field.fieldName?.replace(/\s/g, ""),
          fieldType: field.fieldType,
        });
      }
    });
  });
  return fieldNamesTemp;
}

const ExpressionBuilder = ({ handlePropertyChange }) => {
  const galleryFields = galSessionData.value.map(({ fieldName, fieldType }) => ({
    text: fieldName,
    math: "Fields",
    expressionText: fieldName?.replace(/\s/g, ""),
    fieldType: fieldType,
  }));

  const columnFields = columnsConstructorSessionData.value.map(({ columnName, columnType }) => ({
    text: columnName,
    math: "Fields",
    expressionText: columnName?.replace(/\s/g, ""),
    fieldType: columnType,
  }));

  const getFields = () => {
    if (isGalleryEditor.value) {
      return galleryFields;
    } else if (columnsConstructorSessionData.value.length > 0) {
      return columnFields;
    } else {
      return ExpressionFieldsHelper();
    }
  };

  const getCurrentPropertyValues = () => {
    if (isGalleryEditor.value) {
      return {
        ...selectedGalleryFieldPropertyValues.value,
        ...editedPropertyValues.value?.[selectedFieldName.value],
      };
    } else if (columnFields.length > 0) {
      return {
        ...selectedColumnFieldPropertyValues.value,
        ...editedColumnPropertyValues.value?.[selectedColumnIndex.value],
      };
    } else {
      return selectedFieldPropertyValues.value;
    }
  };

  const [selectedComponent, setSelectedComponent] = useState("");
  const [functionDefinition, setFunctionDefinition] = useState("");
  const [masterTreeData, setMasterTreeData] = useState(masterTree);
  const [componentTreeData, setComponentTreeData] = useState(componentTree);
  const [definitionsTreeData, setDefinitionsTreeData] = useState(definitionsTree);
  const fields = getFields();
  const currentPropertyValues = getCurrentPropertyValues();
  const parameterSets = ParameterSetsHelper();
  const datasets = allDatasets.value;
  const [internalExpression, setInternalExpression] = useState(currentPropertyValues.Expression);
  const [editor, setEditor] = useState(null);

  useEffect(() => {
    if (componentTreeData) {
      if (componentTreeData.some((x) => x.text === "Fields")) {
        return;
      }

      setComponentTreeData((prevState) => {
        const fieldsData = { text: "Fields", items: fields };
        return [...prevState, fieldsData];
      });
    }
  }, [fields]);

  const formatParameterSet = (parameterSet) => {
    const newParameters = parameterSet.values.map((parameter) => ({
      math: parameterSet.datasetName + "_" + parameter.value,
      text: "Parameter Value: " + parameter.filterValue,
    }));

    setDefinitionsTreeData((prevState) => {
      const newState = [...prevState];
      newParameters.forEach((newParameter) => {
        if (!newState.some((existingParameter) => existingParameter.math === newParameter.math)) {
          newState.push(newParameter);
        }
      });
      return newState;
    });

    return {
      text: parameterSet.datasetName,
      items: parameterSet.values.map((parameter) => ({
        text: parameterSet.datasetName + "_" + parameter.value,
        math: parameterSet.datasetName + "_" + parameter.value,
        expressionText:
          parameterSet.datasetName.replace(/\s/g, "") + "_" + parameter.value.replace(/\s/g, ""),
        parameterValue: parameter.filterValue,
        datasetID: parameterSet.datasetID,
      })),
    };
  };

  useEffect(() => {
    if (masterTreeData) {
      if (masterTreeData.some((x) => x.text === "Parameters")) {
        return;
      }
      setMasterTreeData((prevState) => {
        const parametersData = {
          text: "Parameters",
          isParent: true,
          expanded: false,
          fontawesomeIcon: faCalculator,
          items: parameterSets?.map((parameterSet) => ({
            text: parameterSet.datasetName,
          })),
        };
        return [...prevState, parametersData];
      });

      parameterSets?.forEach((parameterSet) => {
        setComponentTreeData((prevState) => {
          return [...prevState, formatParameterSet(parameterSet)];
        });
      });
    }
  }, [parameterSets]);

  const handleSubmit = () => {
    // Extract all parameters with the format DatasetName_ParameterName
    const parameters = internalExpression
      ?.match(/\w+_\w+/g)
      ?.join(",")
      ?.split(",");
    let parameterDatasets = [];
    parameters?.forEach((parameter) => {
      const datasetName = parameter.split("_")[0];
      parameterDatasets.push(
        datasets.filter((dataset) => dataset.datasetName.replace(/\s/g, "") === datasetName)[0]?.id
      );
    });

    handlePropertyChange({ propName: "Expression", propValue: internalExpression });
    if (parameters) {
      handlePropertyChange({ propName: "Parameters", propValue: parameters });
    }
    if (parameterDatasets.length > 0) {
      handlePropertyChange({ propName: "ParameterDatasets", propValue: parameterDatasets });
    }
    expressionBuilderVisibility.value = false;
  };

  const handleMasterClick = (e) => {
    if (e.item.isParent) {
      setMasterTreeData((prevState) => {
        const newState = [...prevState];
        newState.forEach((item) => {
          if (item.text === e.item.text) {
            item.expanded = !item.expanded;
            item.isSelected = !item.isSelected;
          }
        });
        return newState;
      });
    } else {
      setSelectedComponent(e.item.text);
      setMasterTreeData((prevState) => {
        const newState = [...prevState];
        newState.forEach((item) => {
          if (item.text === e.item.text) {
            item.isSelected = !item.isSelected;
          }
        });
        return newState;
      });
    }
  };

  const handleChildClick = (e) => {
    setFunctionDefinition(definitionsTreeData.find((x) => x.math === e.item.math)?.text);
  };

  return (
    <div className="expression-page-content">
      <div className="expression-container">
        <strong className="k-mr-2">Expression: </strong>
        <CodeEditor
          fields={fields}
          globalParameters={parameterSets
            .map((set) => {
              const values = set.values.map((parameter) => {
                return {
                  name:
                    set.datasetName.replaceAll(" ", "") + "_" + parameter.value.replaceAll(" ", ""),
                  value: parameter.filterValue,
                };
              });
              return values;
            })
            .flat()}
          internalExpression={internalExpression}
          setInternalExpression={setInternalExpression}
          setEditor={setEditor}
        />
      </div>
      <div className="component-container">
        <div className="component">
          <div className="parent-components-container">
            <TreeView
              className="parent-tree"
              data={masterTreeData}
              onItemClick={(e) => handleMasterClick(e)}
              expandField="expanded"
              expandIcons={true}
              scrollable={true}
              item={(props) => CustomItemRender(props, setInternalExpression, editor)}
            />
          </div>
        </div>
        <div className="component">
          <TreeView
            data={componentTreeData
              .filter((x) => x.text === selectedComponent)
              .flatMap((x) => x.items)
              .map((x) => ({
                text: x.text,
                math: x.math,
                expressionText: x.expressionText,
                parameterValue: x.parameterValue,
              }))}
            onItemClick={(e) => handleChildClick(e)}
            item={(props) => CustomItemRender(props, setInternalExpression, editor)}
          />
        </div>
        <div className="component">{functionDefinition}</div>
      </div>
      <div className="expression-builder-buttons">
        <Button
          themeColor={"success"}
          onClick={() => handleSubmit()}
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
        >
          Save
        </Button>
        <Button
          themeColor={"info"}
          onClick={() => setInternalExpression("")}
          className="k-ml-3 k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
        >
          Clear
        </Button>
      </div>
    </div>
  );
};

export default ExpressionBuilder;
